import { create } from 'zustand';
import { Decimal } from 'decimal.js';

export interface CartItem {
  productId: string;
  productName: string;
  category: string;
  quantity: number;
  unitPrice: Decimal;
  price: Decimal;
  discountPercent: Decimal;
  /** Zero-rated goods are sold at the same price but carry no VAT. */
  zeroRated?: boolean;
}

interface CheckoutState {
  items: CartItem[];
  paymentMethod: 'CASH' | 'CARD' | 'MOBILE' | 'ACCOUNT';
  customerAccountId?: string;
  deliveryAddress?: string;
  loaves10Count: number;

  // Computed totals
  subtotal: Decimal;
  discountAmount: Decimal;
  vat: Decimal;
  total: Decimal;
  deliveryFee: Decimal;

  // Actions
  addItem: (item: Omit<CartItem, 'price'>) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  updateDiscount: (productId: string, discountPercent: Decimal) => void;
  setPaymentMethod: (method: 'CASH' | 'CARD' | 'MOBILE' | 'ACCOUNT') => void;
  setCustomerAccountId: (accountId?: string) => void;
  setDeliveryAddress: (address?: string) => void;
  clear: () => void;

  // Recalculate totals
  recalculate: () => void;
}

const VAT_RATE = new Decimal(0.15);
// 15/115 — the VAT portion of a tax-inclusive price
const VAT_FRACTION_OF_GROSS = VAT_RATE.div(VAT_RATE.add(1));
const LOAVES_DISCOUNT_THRESHOLD = 10;
const LOAVES_DISCOUNT_PERCENT = new Decimal(0.1);

export const useCheckoutStore = create<CheckoutState>((set, get) => ({
  items: [],
  paymentMethod: 'CASH',
  subtotal: new Decimal(0),
  discountAmount: new Decimal(0),
  vat: new Decimal(0),
  total: new Decimal(0),
  deliveryFee: new Decimal(0),
  loaves10Count: 0,

  addItem: (item: Omit<CartItem, 'price'>) => {
    set((state) => {
      const existingItem = state.items.find((i) => i.productId === item.productId);

      let newItems: CartItem[];
      if (existingItem) {
        newItems = state.items.map((i) =>
          i.productId === item.productId
            ? {
                ...i,
                quantity: i.quantity + item.quantity,
                price: i.unitPrice.mul(i.quantity + item.quantity).mul(
                  new Decimal(1).minus(i.discountPercent.div(100)),
                ),
              }
            : i,
        );
      } else {
        const price = item.unitPrice.mul(item.quantity).mul(
          new Decimal(1).minus(item.discountPercent.div(100)),
        );
        newItems = [
          ...state.items,
          {
            ...item,
            price,
          },
        ];
      }

      return { items: newItems };
    });

    get().recalculate();
  },

  removeItem: (productId: string) => {
    set((state) => ({
      items: state.items.filter((i) => i.productId !== productId),
    }));
    get().recalculate();
  },

  updateQuantity: (productId: string, quantity: number) => {
    set((state) => ({
      items: state.items.map((i) =>
        i.productId === productId
          ? {
              ...i,
              quantity,
              price: i.unitPrice.mul(quantity).mul(
                new Decimal(1).minus(i.discountPercent.div(100)),
              ),
            }
          : i,
      ),
    }));
    get().recalculate();
  },

  updateDiscount: (productId: string, discountPercent: Decimal) => {
    set((state) => ({
      items: state.items.map((i) =>
        i.productId === productId
          ? {
              ...i,
              discountPercent,
              price: i.unitPrice.mul(i.quantity).mul(
                new Decimal(1).minus(discountPercent.div(100)),
              ),
            }
          : i,
      ),
    }));
    get().recalculate();
  },

  setPaymentMethod: (method) => {
    set({ paymentMethod: method });
  },

  setCustomerAccountId: (accountId) => {
    set({ customerAccountId: accountId });
  },

  setDeliveryAddress: (address) => {
    set({ deliveryAddress: address });
  },

  clear: () => {
    set({
      items: [],
      paymentMethod: 'CASH',
      customerAccountId: undefined,
      deliveryAddress: undefined,
      subtotal: new Decimal(0),
      discountAmount: new Decimal(0),
      vat: new Decimal(0),
      total: new Decimal(0),
      deliveryFee: new Decimal(0),
      loaves10Count: 0,
    });
  },

  recalculate: () => {
    const state = get();
    let subtotal = new Decimal(0);
    let loavesCount = 0;

    for (const item of state.items) {
      subtotal = subtotal.add(item.price);
      if (
        item.category === 'Loaves' ||
        (item.productName || '').toLowerCase().includes('loaf')
      ) {
        loavesCount += item.quantity;
      }
    }

    // Apply 10% discount if >= 10 loaves
    let discountAmount = new Decimal(0);
    let finalSubtotal = subtotal;

    if (loavesCount >= LOAVES_DISCOUNT_THRESHOLD) {
      discountAmount = subtotal.mul(LOAVES_DISCOUNT_PERCENT);
      finalSubtotal = subtotal.minus(discountAmount);
    }

    // Calculate delivery fee
    let deliveryFee = new Decimal(0);
    if (state.deliveryAddress) {
      if (loavesCount >= 20) {
        deliveryFee = new Decimal(0); // Free
      } else if (loavesCount >= 10) {
        deliveryFee = new Decimal(7); // R7
      }
    }

    // Shelf prices include VAT, so what is on the products IS what is charged.
    // VAT is shown on the receipt as the portion already inside the total, and
    // only the standard-rated lines contribute any of it.
    const total = finalSubtotal.add(deliveryFee);
    const standardRated = state.items.reduce(
      (sum, i) => (i.zeroRated ? sum : sum.add(i.price)),
      new Decimal(0),
    );
    const discountRatio = subtotal.gt(0) ? finalSubtotal.div(subtotal) : new Decimal(1);
    const vat = standardRated
      .mul(discountRatio)
      .add(deliveryFee)
      .mul(VAT_FRACTION_OF_GROSS)
      .toDecimalPlaces(2);

    set({
      subtotal,
      discountAmount,
      vat,
      total,
      deliveryFee,
      loaves10Count: loavesCount,
    });
  },
}));
